import { useEffect, useRef, useState } from "react";

interface RobotHeadProps {
  size?: number;
  trackMouse?: boolean;
  className?: string;
}

const IDLE_POSITIONS = [
  { x: 0, y: 0 },
  { x: 3, y: -1 },
  { x: -3, y: 1 },
  { x: 2, y: 2 },
  { x: -2, y: -2 },
  { x: 0, y: 3 },
  { x: -3, y: 0 },
  { x: 3, y: -2 },
];

export function RobotHead({ size = 60, trackMouse = false, className = "" }: RobotHeadProps) {
  const [eyeOffset, setEyeOffset] = useState({ x: 0, y: 0 });
  const [blinking, setBlinking] = useState(false);
  const ref = useRef<SVGSVGElement>(null);
  const hasMouseRef = useRef(false);

  // Mouse tracking
  useEffect(() => {
    if (!trackMouse) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!ref.current) return;
      hasMouseRef.current = true;
      const rect = ref.current.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = e.clientX - cx;
      const dy = e.clientY - cy;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const maxOffset = 4;
      const scale = Math.min(maxOffset / (dist || 1), 0.06);
      setEyeOffset({ x: dx * scale, y: dy * scale });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [trackMouse]);

  // Idle look-around (for mobile / no mouse)
  useEffect(() => {
    let idleIdx = 0;
    const interval = setInterval(() => {
      if (hasMouseRef.current) return;
      idleIdx = (idleIdx + 1) % IDLE_POSITIONS.length;
      setEyeOffset(IDLE_POSITIONS[idleIdx]);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // Blinking
  useEffect(() => {
    const scheduleBlink = () => {
      const delay = 2500 + Math.random() * 3000;
      return setTimeout(() => {
        setBlinking(true);
        setTimeout(() => setBlinking(false), 150);
        timer = scheduleBlink();
      }, delay);
    };
    let timer = scheduleBlink();
    return () => clearTimeout(timer);
  }, []);

  const accent = "var(--kis-accent, #E84855)";
  const eyeH = blinking ? 2 : 18;
  const eyeY = blinking ? 39 : 31;

  return (
    <svg
      ref={ref}
      viewBox="0 0 80 80"
      width={size}
      height={size}
      className={className}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Head background */}
      <rect x="10" y="20" width="60" height="48" rx="14" fill={accent} />

      {/* Antenna */}
      <line x1="40" y1="20" x2="40" y2="8" stroke={accent} strokeWidth="3" strokeLinecap="round" />
      <circle cx="40" cy="6" r="4" fill={accent} />

      {/* Ears */}
      <rect x="4" y="34" width="8" height="16" rx="4" fill={accent} />
      <rect x="68" y="34" width="8" height="16" rx="4" fill={accent} />

      {/* Eye whites */}
      <rect x="19" y={eyeY} width="18" height={eyeH} rx="5" fill="white">
        <animate attributeName="height" dur="0.15s" fill="freeze" />
      </rect>
      <rect x="43" y={eyeY} width="18" height={eyeH} rx="5" fill="white">
        <animate attributeName="height" dur="0.15s" fill="freeze" />
      </rect>

      {/* Pupils */}
      {!blinking && (
        <>
          <circle cx={28 + eyeOffset.x} cy={40 + eyeOffset.y} r="5" fill="#1a1a2e" />
          <circle cx={52 + eyeOffset.x} cy={40 + eyeOffset.y} r="5" fill="#1a1a2e" />
          {/* Highlights */}
          <circle cx={26 + eyeOffset.x} cy={38 + eyeOffset.y} r="1.5" fill="white" />
          <circle cx={50 + eyeOffset.x} cy={38 + eyeOffset.y} r="1.5" fill="white" />
        </>
      )}

      {/* Mouth */}
      <rect x="30" y="56" width="20" height="4" rx="2" fill="white" opacity="0.7" />
    </svg>
  );
}
